import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ReactFlow, {
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  Node,
  Edge,
  Viewport,
  ConnectionMode,
  MarkerType,
} from 'reactflow';
import { Loader2, AlertCircle, ExternalLink } from '@/components/icons';
import { getPublicDiagram } from '../../services/diagramService';
import { TechNode, GroupNode, StickyNoteNode, TextNode } from '../../features/diagrams';
import { useTheme } from '../../App';
import 'reactflow/dist/style.css';
import { EDGE_TYPES } from '../../features/diagrams';

const nodeTypes = {
  techNode: TechNode,
  groupNode: GroupNode,
  stickyNote: StickyNoteNode,
  textNode: TextNode,
};

interface EmbedDiagramData {
  id: string;
  name: string;
  description: string | null;
  nodes: Node[];
  edges: Edge[];
  viewport: Viewport | null;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * URL Parameters supported:
 * - theme: 'dark' | 'light' | 'auto' (default: 'auto')
 * - controls: 'true' | 'false' (default: 'false')
 * - minimap: 'true' | 'false' (default: 'false')
 * - background: 'true' | 'false' (default: 'true')
 * - zoom: 'true' | 'false' (default: 'true')
 * - pan: 'true' | 'false' (default: 'true')
 * - branding: 'true' | 'false' (default: 'true')
 */
function EmbedDiagramViewerContent() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [searchParams] = useSearchParams();
  const [diagram, setDiagram] = useState<EmbedDiagramData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Parse URL parameters for customization
  const options = useMemo(() => {
    const themeParam = searchParams.get('theme') || 'auto';
    const systemDark = typeof window !== 'undefined' 
      ? window.matchMedia('(prefers-color-scheme: dark)').matches 
      : true;
    
    return {
      theme: themeParam === 'auto' ? (systemDark ? 'dark' : 'light') : themeParam,
      showControls: searchParams.get('controls') === 'true',
      showMinimap: searchParams.get('minimap') === 'true',
      showBackground: searchParams.get('background') !== 'false',
      allowZoom: searchParams.get('zoom') !== 'false',
      allowPan: searchParams.get('pan') !== 'false',
      showBranding: searchParams.get('branding') !== 'false',
    };
  }, [searchParams]);

  const isDark = options.theme === 'dark';
  const { setTheme } = useTheme();

  // Drive the global theme from the ?theme= URL param (single class-writer).
  useEffect(() => {
    setTheme(isDark);
  }, [isDark, setTheme]);

  useEffect(() => {
    const fetchDiagram = async () => {
      if (!shareToken) {
        setError('Invalid share link');
        setIsLoading(false);
        return;
      }

      try {
        const data = await getPublicDiagram(shareToken);
        // Remove interactive callbacks from nodes (view-only)
        const viewOnlyNodes = data.nodes.map((node) => ({
          ...node,
          data: {
            ...node.data,
            onSnapshot: undefined,
          },
          draggable: false,
          selectable: false,
          connectable: false,
        }));
        setDiagram({ ...data, nodes: viewOnlyNodes });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Diagram not found or not public');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDiagram();
  }, [shareToken]);

  if (isLoading) {
    return (
      <div className="flex h-full min-h-[200px] w-full items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading diagram...</p>
        </div>
      </div>
    );
  }

  if (error || !diagram) {
    return (
      <div className="flex h-full min-h-[200px] w-full items-center justify-center bg-background">
        <div className="mx-4 max-w-sm rounded-xl border border-border bg-card p-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-6 w-6 text-destructive" />
          </div>
          <h1 className="mb-1 text-lg font-semibold text-foreground">Diagram Not Found</h1>
          <p className="text-sm text-muted-foreground">
            {error || 'This diagram may have been deleted or is not public.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full min-h-[200px] w-full bg-background">
      <ReactFlow
        nodes={diagram.nodes}
        edges={diagram.edges}
        defaultViewport={diagram.viewport || undefined}
        nodeTypes={nodeTypes}
        edgeTypes={EDGE_TYPES}
        fitView
        fitViewOptions={{ padding: 0.15 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag={options.allowPan}
        zoomOnScroll={options.allowZoom}
        zoomOnPinch={options.allowZoom}
        zoomOnDoubleClick={options.allowZoom}
        preventScrolling={true}
        connectionMode={ConnectionMode.Loose}
        className="bg-background transition-colors duration-200"
        proOptions={{ hideAttribution: true }}
        defaultEdgeOptions={{
          type: 'smoothstep',
          style: { strokeWidth: 2 },
          markerEnd: { type: MarkerType.ArrowClosed },
        }}
      >
        {options.showBackground && (
          <Background
            color="var(--border)"
            gap={20}
            variant={BackgroundVariant.Dots}
          />
        )}
        {options.showControls && (
          <Controls 
            showInteractive={false}
            className={isDark ? 'react-flow__controls-dark' : ''}
          />
        )}
        {options.showMinimap && (
          <MiniMap
            nodeColor="var(--muted-foreground)"
            maskColor="color-mix(in srgb, var(--background) 80%, transparent)"
            className="bg-card rounded-lg"
          />
        )}
      </ReactFlow>

      {/* Branding watermark */}
      {options.showBranding && (
        <a
          href={`${window.location.origin}/share/${shareToken}`}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute bottom-2 right-2 z-10 flex items-center gap-1.5 rounded-md border border-border bg-card/90 px-2.5 py-1 text-xs font-medium text-muted-foreground backdrop-blur-sm transition-all hover:scale-105 hover:text-foreground"
        >
          <span>Stack Sketch</span>
          <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </div>
  );
}

export default function EmbedDiagramViewer() {
  return (
    <ReactFlowProvider>
      <div className="w-screen h-screen overflow-hidden">
        <EmbedDiagramViewerContent />
      </div>
    </ReactFlowProvider>
  );
}
